import { useState, useRef, useEffect } from 'react';
import { Sparkles, Wand2, Loader2, X, Check, RefreshCw } from 'lucide-react';
import { generateWithAI, type GenerateType } from '../lib/api';
import type { ResumeData } from '../types/resume';

interface AiWriteButtonProps {
  type: GenerateType;
  resume: ResumeData;
  context: string;
  label?: string;
  onApply: (text: string) => void;
  placeholder?: string;
}

export function AiWriteButton({
  type,
  resume,
  context,
  label = 'Write with AI',
  onApply,
  placeholder = 'e.g. quantify achievements, make it technical, keep it under 50 words',
}: AiWriteButtonProps) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [generated, setGenerated] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    try {
      const { result } = await generateWithAI({ type, resume, context, prompt: prompt || undefined });
      setGenerated(typeof result === 'string' ? result : result.join('\n'));
    } catch (err: any) {
      setError(err.message || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    onApply(generated);
    setOpen(false);
    setGenerated('');
    setPrompt('');
  };

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        onClick={() => setOpen((s) => !s)}
        type="button"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 px-2.5 py-1 rounded-md transition-colors"
      >
        <Sparkles className="h-3.5 w-3.5" />
        {label}
      </button>

      {open && (
        <div
          ref={popoverRef}
          className="rx-dark absolute z-50 mt-2 w-80 bg-white rounded-xl border border-slate-200 p-4 space-y-3"
          style={{ right: 0 }}
        >
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
              <Wand2 className="h-4 w-4 text-primary-600" /> AI Writer
            </h4>
            <button
              onClick={() => setOpen(false)}
              className="text-slate-400 hover:text-slate-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={placeholder}
            rows={3}
            className="w-full rounded-lg border-slate-200 px-3 py-2 text-xs focus:border-primary-500 focus:ring-primary-500"
          />

          <button
            onClick={handleGenerate}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-3 py-2 rounded-lg text-xs font-medium transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            {loading ? 'Writing...' : 'Generate'}
          </button>

          {error && (
            <div className="text-xs text-red-600 bg-red-50 rounded-lg p-2">{error}</div>
          )}

          {generated && (
            <div className="space-y-2">
              <div className="max-h-40 overflow-y-auto rounded-lg border border-slate-100 bg-slate-50 p-3 text-xs text-slate-700 whitespace-pre-line">
                {generated}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleApply}
                  className="flex-1 flex items-center justify-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                >
                  <Check className="h-3.5 w-3.5" /> Use this
                </button>
                <button
                  onClick={handleGenerate}
                  disabled={loading}
                  className="flex items-center justify-center gap-1 bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-60"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
